// src/components/rcv/asociaciones/FormularioAsociacion.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, FormControl, InputLabel, 
  Select, MenuItem, Button, TextField, ToggleButtonGroup, 
  ToggleButton, CircularProgress, Autocomplete
} from '@mui/material';
import Save from '@mui/icons-material/Save';
import Edit from '@mui/icons-material/Edit';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import { asociacionesService } from '../../../services/asociacionesService';
import { entidadesService } from '../../../services/entidadesService';
import { formatearRut, formatearMonto, formatearFecha, getTipoDocLabel } from '@/utils/formatters';
import ImpuestosAdicionalesSection from './ImpuestosAdicionalesSection';

import DocumentoButton from '../DocumentoButton';

const FormularioAsociacion = ({
  documentoSeleccionado,
  setDocumentoSeleccionado,
  mapaAsociaciones,
  cuentas,
  centros,
  clasificadores,
  rutEmpresa,
  periodo,
  onAsociacionGuardada,
  onEntidadDialogOpen
}) => {
  // Estado para formulario
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState('');
  const [centroSeleccionado, setCentroSeleccionado] = useState('');
  const [clasificadorSeleccionado, setClasificadorSeleccionado] = useState('');
  const [entidadSeleccionada, setEntidadSeleccionada] = useState(null);
  const [tipoAsignacion, setTipoAsignacion] = useState('centro');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [modoEdicion, setModoEdicion] = useState(false);
  const [asociacionActual, setAsociacionActual] = useState(null);
  
  // Estado para búsqueda de entidades
  const [entidadesResult, setEntidadesResult] = useState([]);
  const [buscandoEntidades, setBuscandoEntidades] = useState(false);
  const entidadSearchTimeout = useRef(null);

  // Cargar entidad
  const cargarEntidad = async (entidadId) => {
    try {
      const entidad = await entidadesService.getEntidad(entidadId);
      setEntidadSeleccionada(entidad);
    } catch (err) {
      console.error("Error al cargar entidad:", err);
      setEntidadSeleccionada(null);
    }
  };
  
  // Obtener sugerencia
  const obtenerSugerencia = useCallback(async (documento) => {
    try {
      const sugerencia = await asociacionesService.getSugerencia(
        rutEmpresa, 
        documento.rut_proveedor, 
        documento.folio
      );
      
      // Asignar valores sugeridos solo si existen
      if (sugerencia) {
        if (sugerencia.cuenta_id) {
          setCuentaSeleccionada(sugerencia.cuenta_id.toString());
        }
        
        if (sugerencia.centro_id) {
          setCentroSeleccionado(sugerencia.centro_id.toString());
          setTipoAsignacion('centro');
        }
        
        if (sugerencia.entidad_id) {
          setTipoAsignacion('entidad');
          cargarEntidad(sugerencia.entidad_id);
        }
        
        if (sugerencia.clasificador_id) {
          setClasificadorSeleccionado(sugerencia.clasificador_id.toString());
        }
      }
    } catch (err) {
      console.error("Error al obtener sugerencia:", err);
    }
  }, [rutEmpresa]);

  // Inicializar form cuando cambia documento seleccionado
  useEffect(() => {
    if (!documentoSeleccionado) return;
    
    setError('');
    
    // Verificar si el documento ya tiene asociación
    const key = `${documentoSeleccionado.rut_proveedor}-${documentoSeleccionado.folio}`;
    const asociacion = mapaAsociaciones[key];
    
    if (asociacion) {
      // Modo edición si ya tiene asociación
      setModoEdicion(true);
      setAsociacionActual(asociacion);
      
      // Establecer valores seleccionados de la asociación existente
      setCuentaSeleccionada(asociacion.cuenta_id ? asociacion.cuenta_id.toString() : '');
      setCentroSeleccionado(asociacion.centro_id ? asociacion.centro_id.toString() : '');
      setClasificadorSeleccionado(asociacion.clasificador_id ? asociacion.clasificador_id.toString() : '');
      
      // Establecer tipo de asignación según los datos
      if (asociacion.entidad_id) {
        setTipoAsignacion('entidad');
        // Cargar detalles de la entidad
        cargarEntidad(asociacion.entidad_id);
      } else {
        setTipoAsignacion('centro');
        setEntidadSeleccionada(null);
      }
    } else {
      // Modo creación si no tiene asociación
      setModoEdicion(false);
      setAsociacionActual(null);
      
      // Limpiar selecciones y obtener sugerencia
      setCuentaSeleccionada('');
      setCentroSeleccionado('');
      setClasificadorSeleccionado('');
      setEntidadSeleccionada(null);
      setTipoAsignacion('centro'); // Por defecto usamos centro
      obtenerSugerencia(documentoSeleccionado);
    }
  }, [documentoSeleccionado, mapaAsociaciones, obtenerSugerencia]);
  
  // Búsqueda de entidades
  const buscarEntidades = (searchTerm) => {
    // Limpiar timeout previo (debounce)
    if (entidadSearchTimeout.current) {
      clearTimeout(entidadSearchTimeout.current);
    }
    
    if (!searchTerm || searchTerm.length < 2) {
      setEntidadesResult([]);
      return;
    }
    
    setBuscandoEntidades(true);
    
    // Debounce para la búsqueda
    entidadSearchTimeout.current = setTimeout(async () => {
      try {
        const results = await entidadesService.buscarEntidades(searchTerm);
        setEntidadesResult(results);
      } catch (err) {
        console.error("Error al buscar entidades:", err);
      } finally {
        setBuscandoEntidades(false);
      }
    }, 300);
  };
  
  // Guardar asociación
  const guardarAsociacion = async () => {
    if (!documentoSeleccionado) {
      setError('Debe seleccionar un documento');
      return;
    }

    if (!cuentaSeleccionada) {
      setError('Debe seleccionar una cuenta contable');
      return;
    }

    if (tipoAsignacion === 'centro' && !centroSeleccionado) {
      setError('Debe seleccionar un centro de costo');
      return;
    }

    if (tipoAsignacion === 'entidad' && !entidadSeleccionada) {
      setError('Debe seleccionar una entidad');
      return;
    }

    setGuardando(true);
    setError('');

    try {
      const datosAsociacion = {
        rut_empresa: rutEmpresa,
        periodo: periodo,
        rut_proveedor: documentoSeleccionado.rut_proveedor,
        folio: documentoSeleccionado.folio,
        cuenta_id: parseInt(cuentaSeleccionada),
        clasificador_id: clasificadorSeleccionado ? parseInt(clasificadorSeleccionado) : null,
        centro_id: tipoAsignacion === 'centro' ? (centroSeleccionado ? parseInt(centroSeleccionado) : null) : null,
        entidad_id: tipoAsignacion === 'entidad' ? (entidadSeleccionada ? entidadSeleccionada.id : null) : null
      };

      if (modoEdicion && asociacionActual) {
        // Actualizar asociación existente
        await asociacionesService.actualizarAsociacion(
          asociacionActual.id,
          datosAsociacion
        );
        onAsociacionGuardada('Asociación actualizada exitosamente');
      } else {
        // Crear nueva asociación
        await asociacionesService.crearAsociacion(datosAsociacion);
        onAsociacionGuardada('Asociación guardada exitosamente');
      }
    } catch (err) {
      setError(err.message || 'Error al guardar asociación');
    } finally {
      setGuardando(false);
    }
  };
  
  // Cancelar edición
  const cancelarEdicion = () => {
    setDocumentoSeleccionado(null);
    setModoEdicion(false);
    setAsociacionActual(null);
    setCuentaSeleccionada('');
    setCentroSeleccionado('');
    setClasificadorSeleccionado('');
    setEntidadSeleccionada(null);
    setTipoAsignacion('centro');
  };

  return (
    <Paper sx={{ 
      height: '500px', 
      p: 3, 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden' 
    }}>
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        {modoEdicion ? "Modificar Asociación" : "Nueva Asociación"}
      </Typography>
      
      {/* Contenedor con scroll */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
        {documentoSeleccionado ? (
          <>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Documento: {getTipoDocLabel(documentoSeleccionado.tipo_doc)} {documentoSeleccionado.folio}
              </Typography>
              <Typography variant="body2">
                Proveedor: {formatearRut(documentoSeleccionado.rut_proveedor, documentoSeleccionado.dv_proveedor)} - {documentoSeleccionado.razon_social}
              </Typography>

              <DocumentoButton
                rutEmisor={documentoSeleccionado.rut_proveedor}
                tipoDoc={documentoSeleccionado.tipo_doc}
                folio={documentoSeleccionado.folio}
                movimientoId={documentoSeleccionado.id}
                rutEmpresa={rutEmpresa}
                onDocumentoCreado={() => {
                  if (typeof onAsociacionGuardada === 'function') {
                    onAsociacionGuardada('Documento asociado correctamente');
                  }
                }}
              />
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Fecha:</Typography>
                  <Typography variant="body2">{formatearFecha(documentoSeleccionado.fecha_docto)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Monto Neto:</Typography>
                  <Typography variant="body2">{formatearMonto(documentoSeleccionado.monto_neto)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Monto Total:</Typography>
                  <Typography variant="body2" fontWeight="medium">{formatearMonto(documentoSeleccionado.monto_total)}</Typography>
                </Grid>
              </Grid>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="cuenta-label">Cuenta Contable</InputLabel>
                  <Select
                    labelId="cuenta-label"
                    id="cuenta-select"
                    name="cuenta"
                    value={cuentaSeleccionada}
                    label="Cuenta Contable *"
                    onChange={(e) => setCuentaSeleccionada(e.target.value)}
                    disabled={guardando}
                    aria-labelledby="cuenta-label"
                  >
                    <MenuItem value="">-- Seleccione Cuenta --</MenuItem>
                    {cuentas.map((cuenta) => (
                      <MenuItem key={cuenta.id} value={cuenta.id.toString()}>
                        {cuenta.codigo} - {cuenta.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Selector de tipo de asignación: Centro o Entidad */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: 1,
                  p: 1
                }}>
                  <Typography variant="body2" sx={{ mr: 2 }} id="tipo-asignacion-label">
                    Asignar a:
                  </Typography>
                  <ToggleButtonGroup
                    value={tipoAsignacion}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setTipoAsignacion(newValue);
                        // Limpiar el otro tipo de asignación
                        if (newValue === 'centro') {
                          setEntidadSeleccionada(null);
                        } else {
                          setCentroSeleccionado('');
                        }
                      }
                    }}
                    size="small"
                    aria-labelledby="tipo-asignacion-label"
                  >
                    <ToggleButton value="centro" aria-label="Centro de Costo">
                      <AccountTreeIcon fontSize="small" sx={{ mr: 1 }} />
                      Centro de Costo
                    </ToggleButton>
                    <ToggleButton value="entidad" aria-label="Entidad (RUT)">
                      <BusinessIcon fontSize="small" sx={{ mr: 1 }} />
                      Entidad (RUT)
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
              
              {/* Mostrar campo según tipo de asignación */}
              {tipoAsignacion === 'centro' ? (
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel id="centro-label">Centro de Costo</InputLabel>
                    <Select
                      labelId="centro-label"
                      id="centro-select"
                      name="centro"
                      value={centroSeleccionado}
                      label="Centro de Costo *"
                      onChange={(e) => setCentroSeleccionado(e.target.value)}
                      disabled={guardando}
                      aria-labelledby="centro-label"
                    >
                      <MenuItem value="">-- Seleccione Centro --</MenuItem>
                      {centros.map((centro) => (
                        <MenuItem key={centro.id} value={centro.id.toString()}>
                          {centro.codigo} - {centro.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Box sx={{ width: '100%' }}>
                    <Autocomplete
                      id="entidad-autocomplete"
                      value={entidadSeleccionada}
                      onChange={(e, newValue) => {
                        setEntidadSeleccionada(newValue);
                      }}
                      onInputChange={(e, newInputValue) => {
                        buscarEntidades(newInputValue);
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      getOptionLabel={(option) => 
                        `${option.rut}-${option.dv}: ${option.nombre}`
                      }
                      options={entidadesResult}
                      loading={buscandoEntidades}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Buscar Entidad (RUT o Nombre) *"
                          required
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {buscandoEntidades ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        // Extraer key de props
                        const { key, ...otherProps } = props;
                        
                        return (
                          <Box component="li" key={option.id || key} {...otherProps}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {formatearRut(option.rut, option.dv)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {option.nombre}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }}
                      noOptionsText={
                        <Box sx={{ textAlign: 'center', py: 1 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            No se encontraron entidades
                          </Typography>
                          <Button 
                            startIcon={<AddIcon />}
                            onClick={onEntidadDialogOpen}
                            size="small"
                          >
                            Crear Nueva Entidad
                          </Button>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onEntidadDialogOpen}
                      >
                        Crear Nueva Entidad
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="clasificador-label">Clasificador</InputLabel>
                  <Select
                    labelId="clasificador-label"
                    id="clasificador-select"
                    name="clasificador"
                    value={clasificadorSeleccionado}
                    label="Clasificador"
                    onChange={(e) => setClasificadorSeleccionado(e.target.value)}
                    disabled={guardando}
                    aria-labelledby="clasificador-label"
                  >
                    <MenuItem value="">-- Sin Clasificador --</MenuItem>
                    {clasificadores.map((clasificador) => (
                      <MenuItem key={clasificador.id} value={clasificador.id.toString()}>
                        {clasificador.codigo} - {clasificador.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {/* Sección de impuestos adicionales */}
            {documentoSeleccionado && documentoSeleccionado.otros_impuestos && documentoSeleccionado.otros_impuestos.length > 0 && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <ImpuestosAdicionalesSection 
                  documentoSeleccionado={documentoSeleccionado}
                  cuentas={cuentas}
                  centros={centros}
                  clasificadores={clasificadores}
                  onSuccess={(message) => {
                    if (typeof onAsociacionGuardada === 'function') {
                      onAsociacionGuardada(message);
                    }
                  }}
                  onError={(message) => setError(message)}
                />
              </Grid>
            )}
            {error && (
              <Box sx={{ mt: 2 }}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              Seleccione un documento de la lista para asociarlo o modificar su asociación existente.
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Botones de acción (siempre visibles al final) */}
      {documentoSeleccionado && (
        <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
          <Button
            variant="outlined"
            color="error"
            onClick={cancelarEdicion}
            disabled={guardando}
            sx={{ minWidth: '120px' }}
            id="cancelar-btn"
            name="cancelar"
          >
            Cancelar
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={guardarAsociacion}
            disabled={
              !cuentaSeleccionada ||
              (tipoAsignacion === 'centro' && !centroSeleccionado) ||
              (tipoAsignacion === 'entidad' && !entidadSeleccionada) ||
              guardando
            }
            startIcon={guardando ? <CircularProgress size={20} /> : modoEdicion ? <Edit /> : <Save />}
            id="guardar-btn"
            name="guardar"
          >
            {guardando 
              ? 'Guardando...' 
              : modoEdicion 
                ? 'Actualizar Asociación' 
                : 'Guardar Asociación'
            }
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default FormularioAsociacion;